import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFund, updateFund, deleteFund } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import {
  ChevronLeft, Pencil, Trash2, Users, TrendingUp, AlertTriangle,
  ShieldAlert, X, Search, FileText, ChevronRight,
} from 'lucide-react';

const statusMeta = {
  PENDING:                 { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', label: 'Pending' },
  KYC_IN_PROGRESS:         { color: '#1D4ED8', bg: '#F8FAFC', border: '#DBEAFE', label: 'KYC' },
  SUITABILITY_IN_PROGRESS: { color: '#475569', bg: '#F8FAFC', border: '#E2E8F0', label: 'Suitability' },
  DOCUMENTS_PENDING:       { color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', label: 'Docs Pending' },
  ACTIVE:                  { color: '#166534', bg: '#F8FAFC', border: '#DCFCE7', label: 'Active' },
  SUSPENDED:               { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Suspended' },
  REJECTED:                { color: '#991B1B', bg: '#FEF2F2', border: '#FECACA', label: 'Rejected' },
};

const suitabilityMeta = {
  SUITABLE:   { color: '#166534', label: 'Suitable' },
  UNSUITABLE: { color: '#991B1B', label: 'Unsuitable' },
  BORDERLINE: { color: '#92400E', label: 'Borderline' },
};

const amlMeta = {
  LOW:    { color: '#166534', label: 'Low' },
  MEDIUM: { color: '#92400E', label: 'Medium' },
  HIGH:   { color: '#991B1B', label: 'High' },
};

const Badge = ({ children, theme }) => (
  <span style={{
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    padding: '3px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const StatusPill = ({ status, theme }) => {
  const meta = statusMeta[status] || {
    color: theme.textSecondary,
    bg: theme.bgInput,
    border: theme.border,
    label: status || '-',
  };

  return (
    <span style={{
      background: meta.bg,
      color: meta.color,
      border: `1px solid ${meta.border}`,
      padding: '3px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {meta.label}
    </span>
  );
};

const FieldLabel = ({ children, theme }) => (
  <label style={{
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: theme.textSecondary,
    marginBottom: 6,
  }}>
    {children}
  </label>
);

const FundDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { theme } = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');

  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg, setEditMsg] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: `1px solid ${theme.border}`,
    fontSize: 13,
    color: theme.textPrimary,
    background: theme.bgInput,
    boxSizing: 'border-box',
    outline: 'none',
  };

  const primaryBtn = {
    background: '#2563EB',
    color: 'white',
    border: '1px solid #2563EB',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };

  const secondaryBtn = {
    background: theme.bgInput,
    color: theme.textSecondary,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };

  const dangerBtn = {
    background: theme.bgInput,
    color: '#991B1B',
    border: '1px solid #FECACA',
    borderRadius: 8,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
  };

  const loadFund = () => getFund(id).then(res => {
    setData(res.data);
    const f = res.data.fund;

    setEditForm({
      fund_name:             f.fund_name || '',
      fund_type:             f.fund_type || '',
      target_investors:      f.target_investors || '',
      isin:                  f.isin || '',
      currency:              f.base_currency || 'EUR',
      legal_form:            f.legal_form || '',
      aum:                   f.aum || '',
      risk_indicator:        f.risk_indicator || '',
      status:                f.status || 'ONBOARDING',
      redemption_window:     f.redemption_window || '',
      minimum_holding_years: f.minimum_holding_years || '',
      cssf_approval_date:    f.cssf_approval_date || '',
      target_aum:            f.target_aum || '',
      nav_per_unit:          f.nav_per_unit || '',
      liquidity_pocket_pct:  f.liquidity_pocket_pct || '',
      leverage_limit_pct:    f.leverage_limit_pct || '',
    });
  });

  useEffect(() => {
    loadFund()
      .catch(() => setError('Fund not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleEdit = async () => {
    setEditLoading(true);
    setEditMsg(null);

    try {
      await updateFund(id, editForm);
      setEditMsg({ type: 'success', text: 'Fund updated successfully.' });
      await loadFund();
      setTimeout(() => {
        setShowEdit(false);
        setEditMsg(null);
      }, 1500);
    } catch (err) {
      setEditMsg({ type: 'error', text: err.response?.data?.error || 'Update failed.' });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirm !== fund?.fund_name) {
      setDeleteMsg({ type: 'error', text: 'Fund name does not match.' });
      return;
    }

    setDeleteLoading(true);
    setDeleteMsg(null);

    try {
      await deleteFund(id);
      navigate('/funds');
    } catch (err) {
      setDeleteMsg({ type: 'error', text: err.response?.data?.error || 'Delete failed.' });
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatAum = (value) => {
    const n = Number(value || 0);
    if (!n) return '-';
    if (n >= 1000000000) return `€${(n / 1000000000).toFixed(1)}B`;
    if (n >= 1000000) return `€${(n / 1000000).toFixed(1)}M`;
    return `€${n.toLocaleString('en-LU')}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.textTertiary, fontSize: 14 }}>Loading fund...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#991B1B', fontSize: 14 }}>{error}</p>
      </div>
    );
  }

  const { fund, investors, stats } = data;

  const filtered = investors.filter(inv => {
    if (filter !== 'ALL' && inv.onboarding_status !== filter) return false;

    if (search) {
      const q = search.toLowerCase();
      return (
        (inv.full_name || '').toLowerCase().includes(q) ||
        (inv.email || '').toLowerCase().includes(q)
      );
    }

    return true;
  });

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => navigate('/funds')} style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: theme.textSecondary,
          fontSize: 12,
          fontWeight: 600,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          marginBottom: 12,
        }}>
          <ChevronLeft size={14} />
          Funds
        </button>

        <div style={{
          background: theme.bgCard,
          border: `1px solid ${theme.border}`,
          borderRadius: 10,
          boxShadow: theme.shadowSm,
          padding: isMobile ? 16 : 20,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h1 style={{
                  margin: 0,
                  fontSize: isMobile ? 20 : 23,
                  fontWeight: 700,
                  color: theme.textPrimary,
                }}>
                  {fund.fund_name}
                </h1>
                <Badge theme={theme}>{fund.fund_type}</Badge>
                {fund.legal_form && <Badge theme={theme}>{fund.legal_form}</Badge>}
              </div>

              <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textSecondary }}>
                {fund.fund_manager_name}
                {fund.isin && ` · ${fund.isin}`}
                {fund.base_currency && ` · ${fund.base_currency}`}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setShowEdit(true)} style={primaryBtn}>
                <Pencil size={13} /> Edit
              </button>
              <button onClick={() => setShowDelete(true)} style={dangerBtn}>
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        marginBottom: 16,
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
          borderBottom: `1px solid ${theme.border}`,
        }}>
          {[
            ['Total investors', stats.total, Users],
            ['Active', stats.active, TrendingUp],
            ['AML high risk', stats.aml_high, ShieldAlert],
            ['Unsuitable', stats.unsuitable, AlertTriangle],
          ].map(([label, value, Icon], idx) => (
            <div key={label} style={{
              padding: '16px 18px',
              borderRight: !isMobile && idx < 3 ? `1px solid ${theme.border}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon size={14} color={theme.textTertiary} />
                <p style={{ margin: 0, fontSize: 12, color: theme.textTertiary, fontWeight: 600 }}>
                  {label}
                </p>
              </div>
              <p style={{
                margin: '7px 0 0',
                fontSize: 24,
                fontWeight: 700,
                color: (label === 'AML high risk' || label === 'Unsuitable') && value > 0 ? '#991B1B' : theme.textPrimary,
                lineHeight: 1,
              }}>
                {value}
              </p>
            </div>
          ))}
        </div>

        <div style={{
          padding: '14px 16px',
          display: 'flex',
          gap: 10,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
            <Search size={14} color={theme.textTertiary} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              placeholder="Search investors"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
            {(isMobile
              ? [{ key: 'ALL', label: 'All' }, { key: 'ACTIVE', label: 'Active' }, { key: 'REJECTED', label: 'Rejected' }]
              : [
                  { key: 'ALL', label: 'All' },
                  { key: 'ACTIVE', label: 'Active' },
                  { key: 'PENDING', label: 'Pending' },
                  { key: 'KYC_IN_PROGRESS', label: 'KYC' },
                  { key: 'SUITABILITY_IN_PROGRESS', label: 'Suitability' },
                  { key: 'DOCUMENTS_PENDING', label: 'Documents' },
                  { key: 'REJECTED', label: 'Rejected' },
                ]
            ).map(f => {
              const active = filter === f.key;
              return (
                <button key={f.key} onClick={() => setFilter(f.key)} style={{
                  padding: '7px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: `1px solid ${active ? '#CBD5E1' : theme.border}`,
                  background: active ? '#F8FAFC' : theme.bgInput,
                  color: active ? theme.textPrimary : theme.textSecondary,
                  whiteSpace: 'nowrap',
                }}>
                  {f.label}
                </button>
              );
            })}
          </div>

          <span style={{ fontSize: 12, color: theme.textTertiary, whiteSpace: 'nowrap' }}>
            {filtered.length} of {investors.length}
          </span>
        </div>
      </div>

      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        boxShadow: theme.shadowSm,
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '14px 16px',
          borderBottom: `1px solid ${theme.border}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
        }}>
          <div>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: theme.textPrimary }}>
              Investors
            </p>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textTertiary }}>
              Fund-level investor register
            </p>
          </div>
          <FileText size={17} color={theme.textTertiary} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: theme.textSecondary, margin: 0 }}>
              No investors match your filter.
            </p>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'grid', gap: 8, padding: 10 }}>
            {filtered.map(inv => {
              const suit = inv.suitability ? suitabilityMeta[inv.suitability.suitability_result] : null;
              const aml = inv.aml ? amlMeta[inv.aml.overall_aml_risk] : null;

              return (
                <button key={inv.id} onClick={() => navigate(`/investors/${inv.id}`)} style={{
                  textAlign: 'left',
                  background: theme.bgInput,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 10,
                  padding: 13,
                  cursor: 'pointer',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                        {inv.full_name || <span style={{ color: theme.textTertiary }}>Erased</span>}
                      </p>
                      <p style={{ margin: '3px 0 0', fontSize: 11, color: theme.textSecondary }}>
                        {inv.email || '-'}
                      </p>
                    </div>
                    <StatusPill status={inv.onboarding_status} theme={theme} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>Suitability</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, fontWeight: 600, color: suit ? suit.color : theme.textTertiary }}>
                        {suit ? suit.label : '-'}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>AML</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, fontWeight: 600, color: aml ? aml.color : theme.textTertiary }}>
                        {aml ? aml.label : '-'}
                      </p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 10, color: theme.textTertiary }}>Joined</p>
                      <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>
                        {new Date(inv.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['Investor', 'Status', 'Suitability', 'AML Risk', 'Joined', ''].map(h => (
                  <th key={h} style={{
                    padding: '11px 16px',
                    textAlign: 'left',
                    fontSize: 10,
                    fontWeight: 600,
                    color: theme.textTertiary,
                    textTransform: 'uppercase',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv, idx) => {
                const suit = inv.suitability ? suitabilityMeta[inv.suitability.suitability_result] : null;
                const aml = inv.aml ? amlMeta[inv.aml.overall_aml_risk] : null;

                return (
                  <tr key={inv.id} onClick={() => navigate(`/investors/${inv.id}`)} style={{
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.border}` : 'none',
                    cursor: 'pointer',
                    transition: 'background 0.12s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '14px 16px' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary }}>
                          {inv.full_name || <span style={{ color: theme.textTertiary }}>Erased</span>}
                        </p>
                        <p style={{ margin: '2px 0 0', fontSize: 11, color: theme.textTertiary }}>
                          {inv.email || '-'}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <StatusPill status={inv.onboarding_status} theme={theme} />
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 600, color: suit ? suit.color : theme.textTertiary }}>
                      {suit ? suit.label : '-'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, fontWeight: 600, color: aml ? aml.color : theme.textTertiary }}>
                      {aml ? aml.label : '-'}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: 12, color: theme.textSecondary }}>
                      {new Date(inv.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <ChevronRight size={15} color={theme.textTertiary} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showEdit && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: theme.bgOverlay,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.bgCard,
            borderRadius: isMobile ? '18px 18px 0 0' : 12,
            width: '100%',
            maxWidth: isMobile ? '100%' : 580,
            maxHeight: isMobile ? '92vh' : '86vh',
            overflowY: 'auto',
            boxShadow: theme.shadowLg,
            border: isMobile ? 'none' : `1px solid ${theme.border}`,
          }}>
            <div style={{
              padding: 20,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              background: theme.bgCard,
              zIndex: 1,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>Edit fund</h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>Update structure and operating values.</p>
              </div>
              <button onClick={() => { setShowEdit(false); setEditMsg(null); }} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                color: theme.textSecondary,
              }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
                {[
                  { label: 'Fund Name', key: 'fund_name', type: 'text' },
                  { label: 'ISIN', key: 'isin', type: 'text' },
                  { label: 'AUM', key: 'aum', type: 'number' },
                  { label: 'Target AUM', key: 'target_aum', type: 'number' },
                  { label: 'NAV per Unit', key: 'nav_per_unit', type: 'number' },
                  { label: 'Min. Holding Years', key: 'minimum_holding_years', type: 'number' },
                  { label: 'Liquidity Pocket %', key: 'liquidity_pocket_pct', type: 'number' },
                  { label: 'Leverage Limit %', key: 'leverage_limit_pct', type: 'number' },
                  { label: 'CSSF Approval Date', key: 'cssf_approval_date', type: 'date' },
                ].map(f => (
                  <div key={f.key}>
                    <FieldLabel theme={theme}>{f.label}</FieldLabel>
                    <input
                      type={f.type}
                      value={editForm[f.key] || ''}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={inputStyle}
                    />
                  </div>
                ))}

                {[
                  { label: 'Fund Type', key: 'fund_type', options: ['UCITS','AIF','ELTIF','RAIF','SIF','SICAR'] },
                  { label: 'Target Investors', key: 'target_investors', options: ['RETAIL','PROFESSIONAL','BOTH'] },
                  { label: 'Currency', key: 'currency', options: ['EUR','USD','GBP','CHF'] },
                  { label: 'Status', key: 'status', options: ['ONBOARDING','ACTIVE','CLOSED','SUSPENDED'] },
                  { label: 'Redemption Window', key: 'redemption_window', options: ['NONE','QUARTERLY','SEMI_ANNUAL','ANNUAL'] },
                  { label: 'Risk Indicator', key: 'risk_indicator', options: ['1','2','3','4','5','6','7'] },
                  { label: 'Legal Form', key: 'legal_form', options: ['SICAV','FCP','SCSp','SCA','SCS'] },
                ].map(f => (
                  <div key={f.key}>
                    <FieldLabel theme={theme}>{f.label}</FieldLabel>
                    <select
                      value={editForm[f.key] || ''}
                      onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                    >
                      <option value="">Select</option>
                      {f.options.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                ))}
              </div>

              {editMsg && (
                <p style={{
                  margin: '14px 0 0',
                  fontSize: 12,
                  fontWeight: 600,
                  color: editMsg.type === 'error' ? '#991B1B' : '#166534',
                }}>
                  {editMsg.text}
                </p>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
                <button onClick={() => { setShowEdit(false); setEditMsg(null); }} style={secondaryBtn}>
                  Cancel
                </button>
                <button onClick={handleEdit} disabled={editLoading} style={{
                  ...primaryBtn,
                  background: editLoading ? '#93C5FD' : '#2563EB',
                  cursor: editLoading ? 'not-allowed' : 'pointer',
                }}>
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDelete && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: theme.bgOverlay,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: theme.bgCard,
            borderRadius: isMobile ? '18px 18px 0 0' : 12,
            width: '100%',
            maxWidth: 480,
            boxShadow: theme.shadowLg,
            border: isMobile ? 'none' : `1px solid ${theme.border}`,
          }}>
            <div style={{
              padding: 20,
              borderBottom: `1px solid ${theme.border}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: theme.textPrimary }}>Delete fund</h2>
                <p style={{ margin: '3px 0 0', fontSize: 12, color: theme.textSecondary }}>This action cannot be undone.</p>
              </div>
              <button onClick={() => { setShowDelete(false); setDeleteMsg(null); setDeleteConfirm(''); }} style={{
                background: theme.bgInput,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                cursor: 'pointer',
                padding: 8,
                display: 'flex',
                color: theme.textSecondary,
              }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <div style={{
                background: '#FEF2F2',
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 16,
                border: '1px solid #FECACA',
              }}>
                <p style={{ margin: 0, fontSize: 13, color: '#991B1B', fontWeight: 600 }}>
                  All associated fund data will be permanently deleted.
                </p>
              </div>

              <p style={{ margin: '0 0 8px', fontSize: 13, color: theme.textPrimary, fontWeight: 500 }}>
                Type <code style={{ background: theme.bgInput, border: `1px solid ${theme.border}`, padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>{fund?.fund_name}</code> to confirm:
              </p>

              <input
                type="text"
                placeholder={fund?.fund_name}
                value={deleteConfirm}
                onChange={e => setDeleteConfirm(e.target.value)}
                style={{ ...inputStyle, borderColor: '#FECACA', marginBottom: 12 }}
              />

              {deleteMsg && (
                <p style={{
                  margin: '0 0 10px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: deleteMsg.type === 'error' ? '#991B1B' : '#166534',
                }}>
                  {deleteMsg.text}
                </p>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => { setShowDelete(false); setDeleteMsg(null); setDeleteConfirm(''); }} style={secondaryBtn}>
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteLoading || deleteConfirm !== fund?.fund_name}
                  style={{
                    ...dangerBtn,
                    background: deleteConfirm === fund?.fund_name ? '#991B1B' : theme.bgInput,
                    color: deleteConfirm === fund?.fund_name ? 'white' : '#991B1B',
                    cursor: deleteLoading || deleteConfirm !== fund?.fund_name ? 'not-allowed' : 'pointer',
                  }}
                >
                  {deleteLoading ? 'Deleting...' : 'Delete Fund'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FundDetail;
